/**
 * RangeDashboard — an ALTERNATE, opt-in project result view that renders the
 * approved "Range dashboard" mockup against real model data.
 *
 * It reuses the existing data layer end-to-end:
 *   • ModelResults.summary_json (impacts, emissions, generation, generators)
 *   • the `scenarios` Floor/Central/Ceiling block + its selectors
 *   • RangeMode / ImpactMode contexts (Central⇄Range, CtG⇄Ops)
 *   • the sensitivity sweep trigger (Run sensitivity affordance)
 *
 * Sign convention: a generator is a benefit (teal); a negative endpoint is a
 * net burden (amber/harm). The fixed generation anchor never gets a band.
 *
 * All chrome is scoped under `.rd` (see RangeDashboard.css) so the mockup's
 * self-contained light/dark palette is faithful and never leaks.
 */

import { useMemo, useState, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import "./RangeDashboard.css";

import { useAuth } from "../../auth/AuthProvider";
import { getHourlyImpacts } from "../../api/impacts";
import { useImpactMode } from "../impact/context/ImpactModeContext";
import { useRangeMode } from "../impact/context/RangeModeContext";
import {
  isRangeReady,
  metricRange,
  metricRatios,
  creditingLadder,
  midpointRanges,
  combineEconomicUsd,
  economicUsdRanges,
  type ScenariosBlock,
  type AnalysisLens,
  type EndpointMetric,
  type MidpointSection,
} from "../impact/domain/scenarios";
import { useGridBaseline } from "../baseline/hooks/useBaseline";
import { getFuelLabel, ENDPOINT_DEFINITIONS } from "../model/domain/definitions";
import type { ImpactTotals } from "../impact/domain/types";
import type { ModelResults } from "../model/domain/types";
import {
  formatImpact,
  toSigFigs,
  formatScientific,
} from "../../utils/format";

import { SummaryHero, type SummaryHeroVM, type EndpointVM } from "./components/SummaryHero";
import { ImpactBreakdown, type BreakdownTab, type BreakdownRow } from "./components/ImpactBreakdown";
import { EmissionsTable, type EmissionRow } from "./components/EmissionsTable";
import { SpatialCard } from "./components/SpatialCard";
import { LifetimeCard } from "./components/LifetimeCard";
import { InterconnectionCard } from "../interconnection/components/InterconnectionCard";
import { GenerationProfileOverlay } from "./components/GenerationProfileOverlay";
import type { LifetimeProjection } from "../lifetime/domain/types";

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface RangeDashboardProps {
  projectName: string;
  techLabel: string;
  capacity: number;
  baCode?: string;
  nercCode?: string;
  analysisYear: number;
  isExisting?: boolean;
  impactTotals: ImpactTotals | null;
  opsTotals?: ImpactTotals | null;
  generationMwh?: number;
  generationMeta?: {
    actual_generation_gwh?: number;
    capacity_factor?: number;
    hours_modeled?: number;
  } | null;
  generationData?: Record<string, { Difference?: number }>;
  avoidedCo2eTonnes?: number;
  scenarios?: ScenariosBlock;
  summaryJson: ModelResults["summary_json"];
  onRunSweep?: () => void;
  sweeping?: boolean;
  /** For the real spatial map + generation-profile fetch. */
  projectId?: string;
  projectLatitude?: number;
  projectLongitude?: number;
  runStatus?: string | null;
  /** Multi-year lifetime projection (future projects); null → card hidden. */
  lifetimeProjection?: LifetimeProjection | null;
  /**
   * Page-level actions (Run model, Edit project, Generate report, …) rendered
   * in the dashboard header. The customer and admin pages pass their own action
   * buttons here so the Range view keeps first-class Run/Edit/Report parity with
   * the Classic layout without forking the dashboard.
   */
  headerActions?: ReactNode;
}

// ---------------------------------------------------------------------------
// Fuel colors (mockup palette — earthy fossil tones)
// ---------------------------------------------------------------------------

const FUEL_COLORS: Record<string, string> = {
  COL: "#5b5b5b", Coal: "#5b5b5b",
  NG: "#8a6d3b", "Natural Gas": "#8a6d3b", Gas: "#8a6d3b",
  OIL: "#b0642f", Oil: "#b0642f", Petroleum: "#b0642f",
  NUC: "#8a7bbf", Nuclear: "#8a7bbf",
  WAT: "#3f7fb0", Hydro: "#3f7fb0",
  OTH: "#9aa6ab", Other: "#9aa6ab",
};
function fuelColor(fuel: string): string {
  if (FUEL_COLORS[fuel]) return FUEL_COLORS[fuel];
  const k = Object.keys(FUEL_COLORS).find((x) =>
    fuel.toLowerCase().includes(x.toLowerCase()),
  );
  return k ? FUEL_COLORS[k] : "#9aa6ab";
}

const fmtGwh = (gwh: number) =>
  gwh >= 1 ? `${toSigFigs(gwh)} GWh` : `${toSigFigs(gwh * 1000)} MWh`;

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function RangeDashboard(props: RangeDashboardProps) {
  const {
    projectName,
    techLabel,
    capacity,
    baCode,
    nercCode,
    analysisYear,
    isExisting,
    impactTotals,
    opsTotals,
    generationMwh,
    generationMeta,
    generationData,
    avoidedCo2eTonnes,
    scenarios,
    summaryJson,
    onRunSweep,
    sweeping,
    projectId,
    projectLatitude,
    projectLongitude,
    runStatus,
    lifetimeProjection,
    headerActions,
  } = props;

  const { token } = useAuth();
  const { mode, setMode } = useImpactMode();
  const { mode: rangeMode, setMode: setRangeMode } = useRangeMode();
  const lens: AnalysisLens = mode === "ops" ? "ops" : "ctg";

  // Generation-profile explore overlay (replaces the standalone gen card).
  const [genOpen, setGenOpen] = useState(false);

  const status = scenarios?.status;
  const rangeViewable = status === "complete" || status === "stale";
  const rangeReady = rangeMode === "range" && isRangeReady(scenarios);

  const { data: baseline } = useGridBaseline(baCode);
  const gridGenMwh = baseline?.total_generation_mwh;

  const effectiveTotals =
    mode === "ops" && opsTotals ? opsTotals : impactTotals;

  // —— endpoint metric formatting ——
  const fmtMetric = (metric: EndpointMetric, v: number) =>
    metric === "usd" ? formatImpact(v, "usd") : toSigFigs(v);

  const endpoints = useMemo<EndpointVM[]>(() => {
    if (!effectiveTotals) return [];
    const specs: Array<{
      cls: "h" | "b" | "e";
      label: string;
      metric: EndpointMetric;
      value: number;
      benefitSub: string;
      burdenSub: string;
      gridPerMwh?: number | null;
    }> = [
      {
        cls: "h", label: "Human health", metric: "dalys",
        value: effectiveTotals.dalys,
        benefitSub: "DALYs avoided", burdenSub: "DALYs net impact",
        gridPerMwh: baseline?.human_health_dalys_per_mwh,
      },
      {
        cls: "b", label: "Ecosystem", metric: "pdfs",
        value: effectiveTotals.pdfs,
        benefitSub: "species·yr protected", burdenSub: "species·yr net impact",
        gridPerMwh: baseline?.ecosystem_quality_species_yr_per_mwh,
      },
      {
        cls: "e", label: "Economic", metric: "usd",
        value: effectiveTotals.usd,
        benefitSub: "external cost avoided", burdenSub: "external cost (net impact)",
        gridPerMwh: baseline?.resource_scarcity_usd_per_mwh,
      },
    ];

    return specs.map((s) => {
      const benefit = s.value >= 0;
      const perMwh =
        generationMwh && generationMwh > 0 ? s.value / generationMwh : null;
      // Intensity chip (honest per-MWh figure); "vs grid avg" when a grid
      // baseline exists for this metric.
      let chipLabel: string | undefined;
      let chipValue: string | undefined;
      if (perMwh != null && s.gridPerMwh != null && s.gridPerMwh !== 0) {
        const pct = Math.round((Math.abs(perMwh) / Math.abs(s.gridPerMwh)) * 100);
        chipLabel = "vs grid /MWh";
        chipValue = `${pct}%`;
      } else if (perMwh != null) {
        chipLabel = s.metric === "usd" ? "$/MWh" : "/MWh";
        chipValue =
          s.metric === "usd"
            ? formatImpact(perMwh, "usd")
            : formatScientific(Math.abs(perMwh));
      }

      let whisker: EndpointVM["whisker"];
      if (rangeReady) {
        const r = metricRange(scenarios, lens, s.metric);
        if (r) {
          const span = r.ceiling - r.floor;
          const centralPct =
            span !== 0 ? ((r.central - r.floor) / span) * 100 : 50;
          whisker = {
            floorText: fmtMetric(s.metric, r.floor),
            ceilingText: fmtMetric(s.metric, r.ceiling),
            centralPct,
          };
        }
      }

      return {
        cls: s.cls,
        label: s.label,
        valueText: fmtMetric(s.metric, s.value),
        sub: benefit ? s.benefitSub : s.burdenSub,
        chipLabel,
        chipValue,
        benefit,
        whisker,
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveTotals, baseline, generationMwh, rangeReady, scenarios, lens]);

  // —— generation + displacement ——
  const genGwh =
    generationMeta?.actual_generation_gwh ??
    (generationMwh ? generationMwh / 1000 : undefined);
  const cf = generationMeta?.capacity_factor;

  const displacements = useMemo(() => {
    if (!generationData) return [];
    const total = Object.values(generationData)
      .filter((v) => (v.Difference ?? 0) < -0.001)
      .reduce((s, v) => s + Math.abs(v.Difference ?? 0), 0);
    if (total < 0.001) return [];
    return Object.entries(generationData)
      .filter(([, v]) => (v.Difference ?? 0) < -0.001)
      .map(([fuel, v]) => ({
        fuel,
        label: getFuelLabel(fuel),
        gwh: Math.abs(v.Difference ?? 0),
        pct: (Math.abs(v.Difference ?? 0) / total) * 100,
      }))
      .sort((a, b) => b.gwh - a.gwh);
  }, [generationData]);

  const totalDisplacedGwh = displacements.reduce((s, d) => s + d.gwh, 0);

  const co2Text = useMemo(() => {
    if (avoidedCo2eTonnes == null || avoidedCo2eTonnes <= 0) return undefined;
    return avoidedCo2eTonnes >= 1000
      ? `${toSigFigs(avoidedCo2eTonnes / 1000)} kt`
      : `${toSigFigs(avoidedCo2eTonnes)} t`;
  }, [avoidedCo2eTonnes]);

  const displacedRangeNote = useMemo(() => {
    if (!rangeReady || totalDisplacedGwh <= 0) return undefined;
    const ratios = metricRatios(scenarios, lens, "dalys");
    if (!ratios) return "central realized displacement";
    const lo = totalDisplacedGwh * ratios.floor;
    const hi = totalDisplacedGwh * ratios.ceiling;
    return `central · ranges ${fmtGwh(lo)}–${fmtGwh(hi)} under sweep`;
  }, [rangeReady, totalDisplacedGwh, scenarios, lens]);

  // One consolidated, plain-language crediting explainer for the whole
  // project (replaces the per-metric drill-downs). The discounts are physical,
  // so a single averaged ladder honestly represents all three endpoints.
  const ladder = rangeReady ? creditingLadder(scenarios, lens) : null;
  const ev = scenarios?.realization ?? null;
  const crediting = ladder
    ? {
        creditedPct: ladder.creditedPct,
        fossilNoResponsePct: ladder.fossilNoResponsePct,
        crossBorderDeclinedPct: ladder.crossBorderDeclinedPct,
        crossBorderCreditPct: ladder.crossBorderCreditPct,
        floorPct: ladder.floorPct,
        domestic: ladder.domestic,
        gridLabel: baCode || undefined,
        scope: "project" as const,
        // Top displaced fuels (already on screen in the displacement bar) —
        // grounds the FRF step in this project's own margin ("71% gas, 27% coal").
        fuelMix: displacements.slice(0, 2).map((d) => ({
          label: d.label,
          pct: d.pct,
        })),
        // Measured evidence from the central run, when the sweep carried it.
        frf: ev
          ? {
              median: ev.frf_median,
              p10: ev.frf_p10,
              p90: ev.frf_p90,
              method: ev.marginal_method,
            }
          : null,
        neighbors: (ev?.neighbors ?? []).map((n) => ({
          code: n.area_code,
          delta: n.delta,
          kind: n.delta_kind,
          gwh: n.displaced_gwh_realized,
        })),
        supportingNote:
          "this project's hour-by-hour realization audit (mrf_decomposition.csv), " +
          "marginal fuel mix (marginal_fuel_mix_hourly.csv), and the marginal-model " +
          "validation memo — in its evidence bundle.",
      }
    : undefined;

  const heroVM: SummaryHeroVM = {
    methodNote: "ecoinvent v3.12 · ReCiPe 2016 H/A",
    endpoints,
    crediting,
    generationText: genGwh != null ? `${toSigFigs(genGwh)} GWh` : "—",
    cfText: cf != null ? `${(cf * 100).toFixed(1)}% CF` : undefined,
    displacedText:
      totalDisplacedGwh > 0.001 ? fmtGwh(totalDisplacedGwh) : undefined,
    co2Text,
    displacedRangeNote,
    fuels: displacements.map((d) => ({
      label: d.label,
      pct: d.pct,
      gwhText: fmtGwh(d.gwh),
      color: fuelColor(d.fuel),
    })),
    onExploreGeneration: () => setGenOpen(true),
  };

  // —— impact breakdown tabs ——
  const analysisKey =
    lens === "ops" ? "Operational_Analysis" : "Cradle_to_Grave_Analysis";

  const breakdownTabs = useMemo<BreakdownTab[]>(() => {
    const impacts = summaryJson?.Impacts?.[analysisKey];
    // Endpoint units are sourced from ENDPOINT_DEFINITIONS: each row's number
    // is that category's contribution to the endpoint (DALYs / species·yr / USD).
    const specs: Array<{
      key: "h" | "b" | "e";
      label: string;
      /** short unit next to each value */
      unit: string;
      /** header line derived from the endpoint's unit label */
      endpointLabel: string;
      section: MidpointSection;
      arr: Array<{ Impact_Category?: string; Total_Impact?: number; USD?: number }>;
      useUsd: boolean;
    }> = [
      {
        key: "h", label: "Human health", unit: "DALYs",
        endpointLabel: ENDPOINT_DEFINITIONS.health.unit,
        section: "Public_Health",
        arr: (impacts?.Public_Health as any[]) ?? [], useUsd: false,
      },
      {
        key: "b", label: "Biodiversity", unit: "species·yr",
        endpointLabel: ENDPOINT_DEFINITIONS.ecosystem.unit,
        section: "Biodiversity",
        arr: (impacts?.Biodiversity as any[]) ?? [], useUsd: false,
      },
      {
        key: "e", label: "Economic", unit: "USD",
        endpointLabel: ENDPOINT_DEFINITIONS.economic.unit,
        section: "Economic",
        // Economic externality = monetized health + biodiversity + resource USD,
        // combined per category (parity with the classic breakdown), not the
        // Economic section alone.
        arr: combineEconomicUsd(impacts as any), useUsd: true,
      },
    ];

    return specs.map((s) => {
      // Range bands are CtG-basis only (scenarios carry CtG midpoints).
      const bands =
        rangeReady && lens === "ctg"
          ? s.section === "Economic"
            ? economicUsdRanges(scenarios)
            : midpointRanges(scenarios, s.section)
          : [];
      const bandByCat = new Map(bands.map((b) => [b.category, b]));

      const rows: BreakdownRow[] = s.arr
        .map((e) => {
          const name = e.Impact_Category ?? "—";
          const raw = (s.useUsd ? e.USD : e.Total_Impact) ?? 0;
          const band = bandByCat.get(name);
          // color encodes benefit/harm, so display magnitudes only.
          const fmt = (n: number) =>
            s.useUsd ? formatImpact(Math.abs(n), "usd") : toSigFigs(Math.abs(n));
          return {
            name,
            benefit: raw >= 0,
            central: Math.abs(raw),
            centralText: fmt(raw),
            floor: band ? Math.abs(band.floor) : undefined,
            ceiling: band ? Math.abs(band.ceiling) : undefined,
            rangeText: band ? `${fmt(band.floor)}–${fmt(band.ceiling)}` : undefined,
          };
        })
        .sort((a, b) => b.central - a.central)
        // Economic is the monetized roll-up across all sections — show every
        // category (parity with classic). Health/biodiversity keep a tidy top-8.
        .slice(0, s.section === "Economic" ? Infinity : 8);

      return {
        key: s.key,
        label: s.label,
        unit: s.unit,
        header: `Contribution to ${s.label.toLowerCase()} · ${s.endpointLabel}`,
        rows,
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [summaryJson, analysisKey, rangeReady, lens, scenarios]);

  // —— emissions table ——
  const emissionRatios = rangeReady ? metricRatios(scenarios, lens, "dalys") : null;
  const emissionRows = useMemo<EmissionRow[]>(() => {
    const block = (summaryJson?.Emissions as any)?.[analysisKey];
    if (!block) return [];
    const rows: EmissionRow[] = [];
    for (const cat of Object.keys(block)) {
      const data = block[cat];
      if (!Array.isArray(data)) continue;
      for (const row of data) {
        const name = row?.Name ?? row?.name;
        const value = row?.Value ?? row?.value;
        if (name == null || typeof value !== "number") continue;
        rows.push({
          substance: String(name),
          centralText: toSigFigs(value),
          floorText: emissionRatios
            ? toSigFigs(value * emissionRatios.floor)
            : undefined,
          ceilingText: emissionRatios
            ? toSigFigs(value * emissionRatios.ceiling)
            : undefined,
          unit: String(row?.Unit ?? row?.unit ?? ""),
        });
      }
    }
    return rows
      .sort((a, b) => Math.abs(Number(b.centralText.replace(/[^\d.-]/g, ""))) -
                      Math.abs(Number(a.centralText.replace(/[^\d.-]/g, ""))))
      .slice(0, 12);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [summaryJson, analysisKey, emissionRatios]);

  // —— real daily generation shape (same data path as GenerationProfileCard) ——
  // hourly_impacts is always persisted per run; the daily continuous aggregate
  // can lag, so we roll up the hourly rows ourselves.
  const { data: dailyGen, isLoading: genLoading } = useQuery({
    queryKey: ["rdGenProfileHourly", projectId],
    enabled: !!token && !!projectId && runStatus === "completed",
    staleTime: 1000 * 60 * 10,
    queryFn: async () => {
      const r = await getHourlyImpacts(token!, projectId!, { limit: 20000 });
      const byDay: Record<string, number> = {};
      for (const h of r.data ?? []) {
        const day = (h.timestamp || "").slice(0, 10);
        if (!day) continue;
        byDay[day] = (byDay[day] || 0) + (h.generation_mwh || 0);
      }
      return Object.entries(byDay)
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([date, mwh]) => ({ date, mwh }));
    },
  });

  const lensLabel = lens === "ops" ? "operational" : "cradle-to-grave";
  const lifecycleTag = isExisting == null ? null : isExisting ? "Operating" : "Prospective";

  // Result-view seg handler.
  const handleRange = () => {
    if (rangeViewable) setRangeMode("range");
    else if (status !== "running" && onRunSweep) onRunSweep();
  };

  return (
    <div className="rd">
      {/* controls bar */}
      <div className="rd-bar">
        <div className="controls">
          <div className="seg ghost" role="group" aria-label="Analysis">
            <button
              aria-pressed={lens === "ctg"}
              onClick={() => setMode("ctg")}
            >
              Cradle-to-grave
            </button>
            <button
              aria-pressed={lens === "ops"}
              onClick={() => setMode("ops")}
            >
              Operational
            </button>
          </div>
        </div>
        <div className="controls">
          <div className="seg" role="group" aria-label="Result view">
            <button
              aria-pressed={rangeMode === "central"}
              onClick={() => setRangeMode("central")}
            >
              <span className="d" />
              Central
            </button>
            <button
              aria-pressed={rangeMode === "range"}
              onClick={handleRange}
              /* Range is only meaningful once a sweep result exists. Disable it
                 whenever it isn't viewable — including when NO sweep has run —
                 so it never looks clickable while quietly no-op'ing. Central
                 stays always-clickable; the "Run sensitivity" link below starts
                 a sweep. */
              disabled={!rangeViewable}
              title={
                rangeViewable
                  ? "Reveal the Floor / Central / Ceiling bracket"
                  : status === "running"
                  ? "Sensitivity sweep is running — Range unlocks when it completes"
                  : "Run the sensitivity sweep to enable Range"
              }
            >
              <span className="d" />
              Range
            </button>
          </div>
          {sweeping ? (
            <span className="hint">Enqueuing…</span>
          ) : status === "running" ? (
            <span className="hint">Sweep running…</span>
          ) : !rangeViewable && onRunSweep ? (
            <button className="runlink" onClick={onRunSweep}>
              Run sensitivity
            </button>
          ) : status === "stale" && rangeMode === "range" && onRunSweep ? (
            <button className="runlink" onClick={onRunSweep}>
              stale · re-run
            </button>
          ) : null}
        </div>
      </div>

      {/* page header */}
      <div className="phead">
        <div>
          <div className="crumb">Projects → {projectName}</div>
          <h1>{projectName}</h1>
          <div className="tags">
            <span className="tag">
              {techLabel} · {capacity} MW
            </span>
            {(baCode || nercCode) && (
              <span className="tag">
                {[baCode, nercCode].filter(Boolean).join(" / ")}
              </span>
            )}
            <span className="tag">{analysisYear}</span>
            {lifecycleTag && <span className="tag ok">{lifecycleTag}</span>}
          </div>
        </div>
        {headerActions && <div className="phead-actions">{headerActions}</div>}
      </div>

      {/* range explainer banner */}
      {rangeMode === "range" && rangeReady && (
        <div className="banner">
          <span>
            <b>Range view</b> — every endpoint &amp; midpoint shows its range.
            The ends are cautious vs. simple assumptions, not raw low/high:
          </span>
          <span className="k">Floor</span>most cautious
          <span className="k">Central</span>what we count
          <span className="k">Ceiling</span>simple 1-for-1 claim
        </div>
      )}

      {/* summary hero */}
      <SummaryHero vm={heroVM} />

      {/* impact breakdown + emissions avoided — side by side (they stack on
          narrow screens). The generation profile now opens as an overlay from
          the "Generation — the driver" anchor in the summary hero. */}
      <div className="grid2 rd-breakdown">
        <ImpactBreakdown tabs={breakdownTabs} />
        <EmissionsTable
          rows={emissionRows}
          showRange={rangeReady}
          lensLabel={lensLabel}
        />
      </div>

      {/* lifetime projection — multi-year, native to the range design */}
      {lifetimeProjection && (
        <div style={{ marginTop: 18 }}>
          <LifetimeCard projection={lifetimeProjection} />
        </div>
      )}

      {/* cross-border / interconnection — same data + guard as Classic; only
          renders when the run carried interconnection neighbors. Brings its own
          (dark app) chrome; the wrapper only sets the alt-view width rhythm. */}
      {summaryJson?.interconnection_breakdown?.neighbors &&
        summaryJson.interconnection_breakdown.neighbors.length > 0 && (
          <div className="rd-spatial">
            <InterconnectionCard
              breakdown={summaryJson.interconnection_breakdown as any}
              totalGenerationGwh={genGwh ?? 0}
            />
          </div>
        )}

      {/* spatial — real GeneratorAttributionCard (stays on Central) */}
      <SpatialCard
        attribution={summaryJson?.generator_attribution}
        congestion={summaryJson?.congestion_metadata}
        projectId={projectId}
        projectName={projectName}
        projectLatitude={projectLatitude}
        projectLongitude={projectLongitude}
      />

      {/* generation-profile explore overlay (opened from the driver anchor) */}
      <GenerationProfileOverlay
        open={genOpen}
        onClose={() => setGenOpen(false)}
        daily={dailyGen}
        loading={genLoading}
        annualText={genGwh != null ? `${toSigFigs(genGwh)} GWh` : "—"}
        cfText={cf != null ? `${(cf * 100).toFixed(1)}%` : "—"}
        hoursText={
          generationMeta?.hours_modeled != null
            ? generationMeta.hours_modeled.toLocaleString()
            : "—"
        }
      />

      <div className="foot">
        Modern dashboard. Central ⇄ Range and Cradle-to-grave ⇄
        Operational are live. Floor/Central/Ceiling reflect the sensitivity
        sweep; the generation anchor is fixed and never banded. Spatial stays on
        Central in v1.
      </div>
    </div>
  );
}

export default RangeDashboard;
